<template>
  <v-container fluid class="px-0">
    <v-card elevation="2" class="mx-auto">
      <v-card-title class="white--text green darken-2 py-4 px-5 d-flex align-center">
        <v-icon left color="white">mdi-gas-station</v-icon>
        {{ modoEdicao ? 'Editar Despesa de Combustível' : 'Despesa de Combustível' }}
        <v-spacer></v-spacer>
        <v-btn
            color="white"
            outlined
            :disabled="salvando"
            @click="$router.push({ name: 'financeiro-abastecimentos-despesas' })"
            class="ml-2"
        >
          <v-icon>mdi-arrow-left</v-icon>
          <span class="ml-2 d-none d-sm-flex">Voltar</span>
        </v-btn>
      </v-card-title>

      <v-divider/>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="formularioValido" @submit.prevent="lancarDespesa">
          <v-row>

            <!-- Seção: Informações Básicas -->
            <v-col cols="12">
              <div class="section-header">
                <v-icon left color="primary">mdi-information-outline</v-icon>
                <span class="text-h6 font-weight-medium">Informações Básicas</span>
              </div>
            </v-col>

            <!-- Tipo de Lançamento -->
            <v-col cols="12" md="6">
              <v-select
                  v-model="dados.tiporequisicao"
                  :items="[{ id: 'REQ', tipo: 'Via requisição'}, { id: 'AVU', tipo: 'Avulso (sem requisição)'}]"
                  :loading="carregando"
                  :rules="[rules.required]"
                  item-text="tipo"
                  item-value="id"
                  label="Tipo de Lançamento *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-invoice-arrow-right"
                  clearable
                  :disabled="!permissao('filial_listar')"
                  @change="alteraTipoLancamento()"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhuma filial encontrada</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <!-- Filial -->
            <v-col cols="12" md="6">
              <v-autocomplete
                  v-model="dados.idfilial"
                  :items="filiaisFiltro"
                  :loading="carregando"
                  :rules="[rules.required]"
                  item-text="filial"
                  item-value="idfilial"
                  label="Filial *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-office-building"
                  clearable
                  :disabled="!podeAlterarFilial"
                  @change="alteraFilial();"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhuma filial encontrada</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <template v-if="dados.tiporequisicao === 'REQ'">
              <!-- Requisição -->
              <v-col cols="12">
                <v-autocomplete
                    v-model="dados.idrequisicao"
                    :items="requisicoes"
                    :rules="[rules.required]"
                    item-text="idrequisicao"
                    item-value="idrequisicao"
                    label="Requisição"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-receipt"
                    clearable
                    @change="alteraRequisicao()"
                >
                  <!-- Template customizado para exibir múltiplas informações -->
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        <strong>Req: {{ item.idrequisicao }}</strong> - {{ item.dtmovimento | formataData }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span class="text--primary">{{ item.veiculo }}</span> |
                        <span v-if="item.placa !== '-'" class="text--secondary">{{ item.placa }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <!-- Template para o item selecionado -->
                  <template v-slot:selection="{ item }">
                    Requisição: #{{ item.idrequisicao }}
                  </template>
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Nenhuma requisição encontrada</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Detalhes da Requisição Selecionada -->
              <v-col cols="12" v-if="requisicaoSelecionada">
                <v-expansion-panels v-model="panelExpandido" flat class="expansion-outlined">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="d-flex align-center">
                        <v-icon left color="primary">mdi-information</v-icon>
                        <span class="font-weight-medium">Detalhes da Requisição</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="pt-3">
                        <v-row>
                          <!-- Fornecedor -->
                          <v-col cols="12" md="4">
                            <v-card flat color="grey lighten-5" class="pa-3 v-card-info">
                              <div class="d-flex align-center mb-2">
                                <v-icon left color="orange">mdi-domain</v-icon>
                                <span class="subtitle-2 font-weight-bold">Fornecedor</span>
                              </div>
                              <div class="body-2">
                                <strong>ID:</strong> {{ requisicaoSelecionada.idfornecedor }}
                              </div>
                              <div class="body-2 mt-1">
                                <strong>Razão Social:</strong> {{ requisicaoSelecionada.fornecedor_razaosocial }}
                              </div>
                            </v-card>
                          </v-col>

                          <!-- Colaborador -->
                          <v-col cols="12" md="4">
                            <v-card flat color="grey lighten-5" class="pa-3 v-card-info">
                              <div class="d-flex align-center mb-2">
                                <v-icon left color="green">mdi-account</v-icon>
                                <span class="subtitle-2 font-weight-bold">Colaborador</span>
                              </div>
                              <div class="body-2">
                                <strong>ID:</strong> {{ requisicaoSelecionada.idcolaborador }}
                              </div>
                              <div class="body-2 mt-1">
                                <strong>Nome:</strong> {{ requisicaoSelecionada.colaborador }}
                              </div>
                            </v-card>
                          </v-col>

                          <!-- Veículo -->
                          <v-col cols="12" md="4">
                            <v-card flat color="grey lighten-5" class="pa-3 v-card-info">
                              <div class="d-flex align-center mb-2">
                                <v-icon left color="blue">mdi-car</v-icon>
                                <span class="subtitle-2 font-weight-bold">Veículo</span>
                              </div>
                              <div class="body-2">
                                <strong>Modelo:</strong> {{ requisicaoSelecionada.veiculo }}
                              </div>
                              <div class="body-2 mt-1" v-if="requisicaoSelecionada.placa !== '-'">
                                <strong>Placa:</strong>
                                <v-chip small color="primary" class="ml-1">
                                  {{ requisicaoSelecionada.placa }}
                                </v-chip>
                              </div>
                            </v-card>
                          </v-col>

                          <!-- Informações Adicionais -->
                          <v-col cols="12">
                            <v-card flat color="grey lighten-5" class="pa-3 v-card-info">
                              <div class="d-flex align-center mb-3">
                                <v-icon left color="purple">mdi-calendar-clock</v-icon>
                                <span class="subtitle-2 font-weight-bold">Informações Adicionais</span>
                              </div>

                              <v-row>
                                <!-- Data da Requisição -->
                                <v-col cols="12" sm="6" md="4" v-if="requisicaoSelecionada.dtmovimento">
                                  <div class="info-item">
                                    <div class="info-label">Data da Requisição:</div>
                                    <div class="info-value">{{ requisicaoSelecionada.dtmovimento | formataData }}</div>
                                  </div>
                                </v-col>

                                <!-- Usuário Emissor -->
                                <v-col cols="12" sm="6" md="4" v-if="requisicaoSelecionada.usuario">
                                  <div class="info-item">
                                    <div class="info-label">Usuário Emissor:</div>
                                    <div class="info-value">
                                      {{ requisicaoSelecionada.idusuario }}-{{ requisicaoSelecionada.usuario }}
                                    </div>
                                  </div>
                                </v-col>

                                <!-- Tipo do Abastecimento -->
                                <v-col cols="12" sm="6" md="4" v-if="requisicaoSelecionada.tipoabastecimento">
                                  <div class="info-item">
                                    <div class="info-label">Tipo do Abastecimento:</div>
                                    <div class="info-value">
                                      <v-chip small color="primary" class="white--text">
                                        {{ requisicaoSelecionada.tipoabastecimento }}
                                      </v-chip>
                                    </div>
                                  </div>
                                </v-col>

                                <!-- Observação (ocupa toda a linha) -->
                                <v-col cols="12" v-if="requisicaoSelecionada.observacao">
                                  <div class="info-item">
                                    <div class="info-label mb-2">
                                      <v-icon small class="mr-1">mdi-note-text</v-icon>
                                      Observação:
                                    </div>
                                    <div class="observacao-box">
                                      {{ requisicaoSelecionada.observacao }}
                                    </div>
                                  </div>
                                </v-col>
                              </v-row>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </template>

            <template v-else>
              <!-- Colaboradores -->
<!--              <CampoCliente-->
<!--                  ref="campoColaborador"-->
<!--                  v-model="dados.idcolaborador"-->
<!--                  :idfilial="idfilialColaborador"-->
<!--                  label="Colaborador *"-->
<!--                  :tipo="'U,M,V'"-->
<!--                  required-->
<!--                  :cols="12"-->
<!--                  :md="6"-->
<!--              />-->

              <!-- Fornecedores -->
              <CampoCliente
                  v-model="dados.idfornecedor"
                  :idfilial="idfilialFornecedor"
                  label="Fornecedor *"
                  :noDataMessageDefault="'Digite para buscar fornecedores'"
                  :tipo="'A,F'"
                  required
                  :cols="12"
                  :md="6"
              />

              <!-- Veículo -->
              <v-col cols="12" md="6">
                <v-autocomplete
                    v-model="dados.idveiculo"
                    :items="veiculos"
                    :rules="[rules.required]"
                    item-text="veiculoplaca"
                    item-value="idveiculo"
                    label="Veículo/Placa*"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-car"
                    clearable
                    @change="dados.itens[0].idproduto = dados.idveiculo ? veiculos.filter(v => {return v.idveiculo === dados.idveiculo})[0].idproduto : 0"
                >
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Nenhum veículo encontrado</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
            </template>


            <!-- Seção: Detalhes do Abastecimento -->
            <v-col cols="12" class="pt-6">
              <div class="section-header">
                <v-icon left color="primary">mdi-gas-station</v-icon>
                <span class="text-h6 font-weight-medium">Detalhes do Abastecimento</span>
              </div>
            </v-col>

            <!-- Número do Documento -->
            <v-col cols="12" md="4" class="pb-0">
              <v-text-field
                  v-model.number="dados.documento"
                  :rules="[rules.required, rules.documento]"
                  label="Número do Documento *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-file-document-outline"
                  placeholder="Ex: 123456"
                  counter="20"
                  maxlength="20"
              />
            </v-col>

            <!-- Data do Abastecimento -->
            <v-col cols="12" md="4">
              <v-menu
                  ref="menuData"
                  v-model="menuData"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="dados.dataFormatada"
                      :rules="[rules.required]"
                      label="Data do Abastecimento *"
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      hide-details="auto"
                      outlined
                      dense
                      readonly
                      placeholder="DD/MM/AAAA"
                  />
                </template>

                <v-date-picker
                    v-model="dados.dtabastecimento"
                    locale="pt-br"
                    scrollable
                    no-title
                    :max="dataAtual"
                    @input="onDataSelecionada"
                    color="green darken-2"
                />
              </v-menu>
            </v-col>

            <!-- Combustível -->
            <v-col cols="12" md="4">
              <v-autocomplete
                  v-model="dados.itens[0].idproduto"
                  :items="produtos"
                  :rules="[rules.required]"
                  item-text="produto"
                  item-value="idproduto"
                  label="Tipo de Combustível *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-fuel"
                  clearable
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhum combustível encontrado</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Litros -->
            <v-col cols="12" md="4">
              <v-text-field
                  v-model.number="dados.itens[0].quantidade"
                  :rules="[rules.required, rules.positivo, rules.maxDecimal(3)]"
                  label="Quantidade (Litros) *"
                  type="number"
                  suffix="L"
                  step="0.001"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-gauge"
                  @input="calcularValorTotal"
                  placeholder="0,00"
              />
            </v-col>

            <!-- Valor Unitário -->
            <v-col cols="12" md="4">
              <v-text-field
                  v-model.number="dados.itens[0].valorunitario"
                  :rules="[rules.required, rules.positivo, rules.maxDecimal(3)]"
                  label="Valor Unitário *"
                  prefix="R$"
                  type="number"
                  step="0.001"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-currency-usd"
                  @input="calcularValorTotal"
                  placeholder="0,00"
              />
            </v-col>

            <!-- Valor Total -->
            <v-col cols="12" md="4">
              <v-text-field
                  :value="valorTotalFormatado"
                  label="Valor Total"
                  readonly
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-calculator"
                  class="total-field"
                  :class="valorTotal > 0 ? 'green--text text--darken-2' : ''"
              />
            </v-col>

            <!-- Hodômetro -->
            <template v-if="requisicaoSelecionada && requisicaoSelecionada.placa !== '-'">
              <v-col cols="12" md="6">
                <v-text-field
                    v-model.number="dados.odometro"
                    :rules="[rules.positivo, rules.inteiro]"
                    label="Hodômetro (KM)"
                    suffix="KM"
                    min="0"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-speedometer"
                    placeholder="Ex: 150000"
                    maxlength="6"
                />
              </v-col>

              <!-- Campo vazio para alinhamento -->
              <v-col cols="12" md="6"></v-col>
            </template>

            <!-- Observação -->
            <v-col cols="12">
              <v-textarea
                  v-model="dados.observacao"
                  :rules="[rules.maxLength(500)]"
                  label="Observações"
                  rows="3"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-note-text-outline"
                  counter="500"
                  placeholder="Adicione informações extras sobre o abastecimento..."
              />
            </v-col>

            <!-- Resumo do Lançamento -->
            <v-col cols="12" v-if="mostrarResumo">
              <v-card outlined class="resumo-card">
                <v-card-subtitle class="pb-2 d-flex align-center">
                  <v-icon left color="info">mdi-information</v-icon>
                  <span class="font-weight-medium">Resumo do {{ modoEdicao ? 'Abastecimento' : 'Lançamento' }}</span>
                </v-card-subtitle>
                <v-card-text class="pt-0">
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Combustível</div>
                      <div class="font-weight-medium">{{ produtoSelecionado }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Quantidade</div>
                      <div class="font-weight-medium">{{ formatarNumero(dados.itens[0].quantidade) }} L</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Valor Unit.</div>
                      <div class="font-weight-medium">R$ {{ formatarNumero(dados.itens[0].valorunitario) }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Total</div>
                      <div class="font-weight-bold green--text text--darken-2 text-h6">{{ valorTotalFormatado }}</div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Botões de Ação -->
            <v-col cols="12" class="d-flex justify-end align-center pt-6" style="gap: 12px;">
              <v-btn
                  color="grey"
                  outlined
                  elevation="0"
                  @click="limparFormulario"
                  :disabled="salvando"
              >
                <v-icon left>mdi-eraser</v-icon>
                Limpar
              </v-btn>

              <v-btn
                  color="green darken-2"
                  class="white--text"
                  elevation="2"
                  type="submit"
                  :loading="salvando"
                  :disabled="salvando"
                  x-large
              >
                <v-icon left>mdi-check</v-icon>
                {{ salvando ? 'Salvando...' : (modoEdicao ? 'Atualizar Despesa' : 'Lançar Despesa') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Snackbar para feedback -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        bottom
    >
      <v-icon left color="white">{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            color="white"
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import mixinFilial from "../../../util/mixinFilial";
import CampoCliente from '@/Components/GestaoFrota/Widgets/CampoCliente.vue';

export default {
  mixins: [mixinFilial],
  components: { CampoCliente: CampoCliente },
  data() {
    return {
      panelExpandido: 0,
      tipoLancamento: "REQ",
      formularioValido: false,
      salvando: false,
      carregandoFornecedores: false,
      menuData: false,
      dataAtual: new Date().toISOString().substr(0, 10),
      modoEdicao: false,
      idfilialColaborador: null,
      idfilialFornecedor: null,
      dados: {
        idabastecimento: 0, // ID para edição
        idrequisicao: null,
        tiporequisicao: 'REQ',
        idfilial: null,
        idveiculo: null,
        idfornecedor: null,
        idcolaborador: null,
        dtabastecimento: null,
        dataFormatada: null,
        documento: null,
        odometro: null,
        observacao: null,
        itens: [
          { idproduto: null, quantidade: null, valorunitario: null }
        ]
      },

      search: null,
      search2: null,
      colaboradores: [],
      fornecedores: [],
      veiculos: [],
      produtos: [],
      requisicoes: [],

      aguardarBusca: "",
      aguardarBuscaColaborador: "",
      nomeLimit: 50,
      carregando: false,
      pgLimitInt: 20,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 4000
      },

      // Regras de validação
      rules: {
        required: value => !!value || 'Campo obrigatório',
        positivo: value => value > 0 || 'Valor deve ser maior que zero',
        inteiro: value => Number.isInteger(Number(value)) || 'Deve ser um número inteiro',
        documento: value => {
          if (!value) return 'Campo obrigatório';
          if (value.toString().length > 20) return 'Máximo 20 caracteres';
          return /^[0-9]+$/.test(value) || 'Apenas números são permitidos';
        },
        maxLength: (max) => value => !value || value.length <= max || `Máximo ${max} caracteres`,
        maxDecimal: (decimais) => value => {
          if (!value) return true;
          const partes = value.toString().split('.');
          if (partes.length > 1 && partes[1].length > decimais) {
            return `Máximo ${decimais} casas decimais`;
          }
          return true;
        }
      }
    };
  },

  watch: {
    search: function (val) {
      this.listarColaboradores(val);
    },

    search2: function (val) {
      this.carregarClientes(val);
    },
  },

  computed: {
    ...mapState(["backendUrl", "pgLimit", "timeLimit", "usuario"]),

    requisicaoSelecionada() {
      return this.requisicoes.find(req => req.idrequisicao === this.dados.idrequisicao);
    },

    valorTotal() {
      const litros = parseFloat(this.dados.itens[0].quantidade) || 0;
      const valor = parseFloat(this.dados.itens[0].valorunitario) || 0;
      return litros * valor;
    },

    valorTotalFormatado() {
      return this.valorTotal.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    },

    filiaisFiltro() {
      if (this.filiais.lista) {
        return this.filiais.lista.filter((v) => {
          return (
              v.idfilial !== 4 &&
              v.idfilial !== 6 &&
              v.idfilial !== 12 &&
              v.idfilial !== 13 &&
              v.idfilial !== 25 &&
              v.idfilial !== 29 &&
              v.idfilial !== 33 &&
              v.idfilial !== 37
          );
        });
      } else {
        return [];
      }
    },

    mostrarResumo() {
      return this.dados.itens[0].quantidade &&
          this.dados.itens[0].valorunitario &&
          this.dados.itens[0].idproduto;
    },

    produtoSelecionado() {
      const produto = this.produtos.find(p => p.idproduto === this.dados.itens[0].idproduto);
      return produto ? produto.produto : '-';
    },

    podeAlterarFilial() {
      return this.permissao("filial_listar")
    }
  },

  methods: {
    alteraFilial() {
      this.idfilialColaborador = this.dados.idfilial;
      this.dados.idrequisicao = null;
      this.listarVeiculos();
      this.listarRequisicoes();
    },

    alteraRequisicao(){
      this.panelExpandido=0;
      this.listarRequisicoes();
    },

    alteraTipoLancamento(){
      this.limparFormulario();
      this.listarRequisicoes();
    },

    carregarClientes(val) {
      if (!val || val.length < 2) {
        this.fornecedores = [];
        return;
      }

      clearTimeout(this.aguardarBusca);

      this.aguardarBusca = setTimeout(() => {
        this.carregandoFornecedores = true;
        return axios
            .post(`${this.backendUrl}cliente/listar`, {
              tipo: "A,F",
              limit: 30,
              offset: 0,
              busca: val || null,
            })
            .then((res) => {
              this.fornecedores = res.data.lista || [];
              this.carregandoFornecedores = false;
            })
            .catch((error) => {
              console.error('Erro ao carregar clientes:', error);
              this.fornecedores = [];
              this.carregandoFornecedores = false;
              this.mostrarSnackbar('Erro ao carregar fornecedores', 'error');
            });
      }, this.timeLimit);
    },

    onDataSelecionada(val) {
      this.dados.dataFormatada = this.formatarData(val);
      this.menuData = false;
    },

    formatarData(data) {
      if (!data) return '';
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    },

    formatarNumero(valor) {
      if (!valor && valor !== 0) return '0,000';
      return Number(valor).toFixed(3).replace('.', ',');
    },

    calcularValorTotal() {
      // Força a reatividade do valor total
      this.$forceUpdate();
    },

    async lancarDespesa() {
      if (!this.$refs.form.validate()) {
        this.mostrarSnackbar('Por favor, corrija os erros no formulário', 'error');
        return;
      }

      this.salvando = true;

      try {
        const payload = {
          ...this.dados,
          acao: this.modoEdicao ? "U" : "I",
          obervacao: this.dados.observacao, // Mantido como estava na API
          documento: parseInt(this.dados.documento),
          odometro: parseInt(this.dados.odometro),
        };

        await axios.post(`${this.backendUrl}tms/veiculo/inserir`, payload);

        const mensagem = this.modoEdicao
            ? 'Despesa de combustível atualizada com sucesso!'
            : 'Despesa de combustível lançada com sucesso!';

        this.mostrarSnackbar(mensagem, 'success');

        this.$router.push({ name: 'financeiro-abastecimentos-despesas' })

        if (!this.modoEdicao) {
          this.$refs.form.reset();
          // this.limparFormulario();
        }
      } catch (error) {
        console.log('Erro ao salvar despesa:', error.response.data.erro);
        const mensagem = error.response?.data?.erro || 'Erro ao salvar despesa. Tente novamente.';
        this.mostrarSnackbar(mensagem, 'error');
      } finally {
        this.salvando = false;
      }
    },

    limparFormulario() {
      const tiporequisicao = this.dados.tiporequisicao;
      this.dados = {
        idabastecimento: 0,
        idrequisicao: null,
        tiporequisicao: tiporequisicao,
        idfilial: this.usuario.idfilial,
        idveiculo: null,
        idfornecedor: null,
        idcolaborador: null,
        dtabastecimento: null,
        dataFormatada: null,
        documento: null,
        odometro: null,
        observacao: null,
        itens: [
          { idproduto: null, quantidade: null, valorunitario: null }
        ]
      };
      this.search = null;
      this.fornecedores = [];
    },

    async listarColaboradores(val) {
      // Limpa timeout anterior para evitar múltiplas requisições
      clearTimeout(this.aguardarBuscaColaborador);

      this.aguardarBuscaColaborador = setTimeout(async () => {
        this.carregandoColaboradores = true;

        try {
          const res = await axios.post(`${this.backendUrl}cliente/listar`, {
            tipo: 'U,M,V',
            limit: 30,
            offset: 0,
            busca: val || null,
            idfilial: this.idfilialColaborador || null,
          });

          this.colaboradores = res.data?.lista || [];

        } catch (error) {
          console.error('Erro ao carregar colaboradores:', error);
          this.colaboradores = [];
          this.mostrarSnackbar('Erro ao carregar colaboradores', 'error');

        } finally {
          this.carregandoColaboradores = false;
        }
      }, this.timeLimit);
    },

    listarTodosColaboradores(search) {
      this.idfilialColaborador = null;
      this.listarColaboradores(search);
    },

    async listarProdutos() {
      this.carregando = true;
      try {
        const res = await axios.post(`${this.backendUrl}tms/produto/listar`, {
          limit: 9999,
          offset: 0,
          busca: null,
        });

        this.produtos = res.data || [];
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        this.mostrarSnackbar('Erro ao carregar combustíveis', 'error');
      } finally {
        this.carregando = false;
      }
    },

    async listarVeiculos() {
      this.carregando = true;
      try {
        const res = await axios.post(`${this.backendUrl}tms/veiculo/listar`, {
          idfilial: this.dados.idfilial || null,
        });

        this.dados.itens[0].idproduto = null;
        this.veiculos = res.data || [];
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        this.mostrarSnackbar('Erro ao carregar veículos', 'error');
      } finally {
        this.carregando = false;
      }
    },

    async listarRequisicoes() {
      this.carregando = true;

      try {
        const response = await axios.post(`${this.backendUrl}tms/requisicao/listar`, {
          idfilial: this.dados.idfilial || null,
          // idrequisicao: this.dados.idrequisicao || null,
          idstatus: 1,
        });

        if (response.data) {
          this.requisicoes = response.data || [];
          if (this.dados.idrequisicao) {
            // this.$refs.campoColaborador.definirBusca(this.requisicoes[0].colaborador);
            this.dados.idveiculo = this.requisicoes[0].idveiculo;
            this.dados.idfornecedor = this.requisicoes[0].idfornecedor;
            this.dados.idcolaborador = this.requisicoes[0].idcolaborador;
            this.dados.odometro = this.requisicoes[0].odometro;
            this.dados.itens[0].idproduto = this.requisicoes[0].idproduto;
          }
        } else {
          this.requisicoes = [];
        }

      } catch (error) {
        console.error('Erro ao carregar requisições:', error);
        this.mostrarSnackbar('Erro ao carregar requisições', 'error');
        this.requisicoes = [];
      } finally {
        this.carregando = false;
      }
    },

    async carregarAbastecimento(id) {
      try {
        const res = await axios.post(`${this.backendUrl}tms/lancamento/listar`, {
          idlancamento: id
        });

        if (res.data && res.data.length > 0) {
          const abastecimento = res.data[0];

          // Carrega dados básicos
          this.dados = {
            ...this.dados,
            ...abastecimento,
            dataFormatada: this.formatarData(abastecimento.dtabastecimento),
            itens: [{ idproduto: abastecimento.idproduto, quantidade: abastecimento.litros, valorunitario: abastecimento.valorunitario }]
          };

          // Se há itens, carrega o primeiro item
          if (abastecimento.itens && abastecimento.itens.length > 0) {
            this.dados.itens[0] = { ...abastecimento.itens[0] };
          }

          this.modoEdicao = true;

          // Carrega fornecedor se necessário
          if (abastecimento.idfornecedor) {
            await this.carregarFornecedorPorId(abastecimento.idfornecedor);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar abastecimento:', error);
        this.mostrarSnackbar('Erro ao carregar dados do abastecimento', 'error');
      }
    },

    async carregarFornecedorPorId(idfornecedor) {
      try {
        const res = await axios.post(`${this.backendUrl}cliente/listar`, {
          tipo: "A,F",
          busca: (idfornecedor).toString()
        });

        if (res.data && res.data.lista && res.data.lista.length > 0) {
          this.fornecedores = res.data.lista;
        }
      } catch (error) {
        console.error('Erro ao carregar fornecedor:', error);
      }
    },

    mostrarSnackbar(message, type = 'info') {
      const config = {
        success: { color: 'success', icon: 'mdi-check-circle' },
        error: { color: 'error', icon: 'mdi-alert-circle' },
        warning: { color: 'warning', icon: 'mdi-alert' },
        info: { color: 'info', icon: 'mdi-information' }
      };

      this.snackbar = {
        show: true,
        message,
        ...config[type],
        timeout: type === 'error' ? 6000 : 4000
      };
    },


    async init() {
      try {
        await Promise.all([
          this.listarVeiculos(),
          this.listarProdutos()
        ]);

        // Se há ID na rota, é edição
        const idAbastecimento = this.$route.params.id;
        const idRequisicao = this.$route.params.requisicao;

        if (idAbastecimento) {
          await this.carregarAbastecimento(idAbastecimento);
        } if (idRequisicao) {
          this.dados.idrequisicao = idRequisicao;
          this.dados.idfilial = this.$route.params.idfilial;
          this.listarRequisicoes();
        } else {
          this.dados.idfilial = this.usuario.idfilial;
          this.idfilialColaborador = this.usuario.idfilial;
          this.listarRequisicoes();
        }

      } catch (error) {
        console.error('Erro na inicialização:', error);
        this.mostrarSnackbar('Erro ao carregar dados iniciais', 'error');
      }
    }
  },

  created() {
    this.init();
  }
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.resumo-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.total-field {
  font-weight: 600;
}

.total-field >>> .v-input__slot {
  background-color: #f8f9fa;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-card {
  transition: all 0.3s ease;
}

.v-snackbar {
  font-weight: 500;
}

/* Melhorar visibilidade dos campos obrigatórios */
.v-text-field--outlined >>> .v-label--active {
  color: #1976d2 !important;
}

.v-select--outlined >>> .v-label--active {
  color: #1976d2 !important;
}

/* Estilo para campos com erro */
.error--text >>> .v-input__slot {
  border-color: #ff5252 !important;
}

/* Hover nos botões */
.v-btn:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Estilo para campos readonly/disabled */
.v-input--is-readonly >>> .v-input__slot {
  background-color: #f5f5f5;
}


.v-card-info {
  border: 1px solid #e0e0e0;
  min-height: 120px !important;
}

.subtitle-2 {
  color: #424242 !important;
}

.info-item {
  height: 100%;
}

.info-label {
  font-weight: 600;
  color: #616161;
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.info-value {
  color: #424242;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.observacao-box {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.4;
  min-height: 60px;
  white-space: pre-wrap;
}

@media (max-width: 600px) {
  .info-label {
    font-size: 0.8rem;
  }

  .info-value {
    font-size: 0.85rem;
  }

  .observacao-box {
    padding: 10px;
    font-size: 0.85rem;
  }
}

.expansion-outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.expansion-outlined .v-expansion-panel {
  box-shadow: none !important;
}

.expansion-outlined .v-expansion-panel::before {
  box-shadow: none !important;
}
</style>